import isArray from 'lodash-es/isArray'

import { EMPTY_LIST, NOTIFICATION_TYPE } from '../../../constants/common'
import { REQUEST_WIZARD_AI_WIZARD_USER_AUTOFILLED_KEY } from '../Request.constants'

import {
  REQUEST_WIZARD_AI_WIZARD_INIT_STAGE_FIELD_BY_RESPONSE_KEY,
  REQUEST_WIZARD_AI_WIZARD_NOTIFICATION_ID,
  REQUEST_WIZARD_AI_WIZARD_SELECT_KEY_PREFIX,
  REQUEST_WIZARD_FIELD_LINK_TYPE,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_BUILDING,
  REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_FLAT,
} from './RequestWizardForm.constants'
import {
  getCardStageLinkOptions,
  getCardStageVariables,
  isRequestWizardFieldFileType,
} from './RequestWizardForm.utils'

// A field can be auto-filled only when the user has not filled it yet.
function isFieldEmpty(value) {
  if (isArray(value)) {
    return value.length === 0
  }

  return !value
}

export function getAIWizardPayload({ messageUuid, uuid }) {
  return {
    message_id: messageUuid,
    workflow_session_id: uuid || null,
  }
}

// Init stage: profile_id / building_id / flat_id → user / building / flat.
// The BE already returns each suggestion as a { value, label } select item.
// Returns only the fields that are still empty, to be applied in a single update.
// The user option is marked ONLY when the AI also resolved building/flat, so the
// address auto-complete cascade leaves those AI-resolved fields untouched. When
// the AI returns just the user, the marker is omitted so the cascade can still
// resolve the address from the selected user (see useAutoCompleteAddressFormFields).
export function getAIWizardInitStageNextValues({ suggestions, values }) {
  const hasAIResolvedAddress = !!(
    suggestions?.[REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_BUILDING] ||
    suggestions?.[REQUEST_WIZARD_INIT_STAGE_VARIABLE_NAME_FLAT]
  )

  return Object.entries(
    REQUEST_WIZARD_AI_WIZARD_INIT_STAGE_FIELD_BY_RESPONSE_KEY
  ).reduce((acc, [responseKey, fieldName]) => {
    const suggestion = suggestions?.[responseKey]

    if (suggestion && isFieldEmpty(values[fieldName])) {
      acc[fieldName] =
        fieldName === REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER &&
        hasAIResolvedAddress
          ? {
              ...suggestion,
              [REQUEST_WIZARD_AI_WIZARD_USER_AUTOFILLED_KEY]: true,
            }
          : suggestion
    }

    return acc
  }, {})
}

// Card stage: variable `name`s → matching field; `select__card_*` → `link` field.
// Returns a list of [fieldName, value] entries for the still-empty fields.
export function getAIWizardCardStageEntries({ chunk, suggestions, values }) {
  if (suggestions) {
    const linkOptions = getCardStageLinkOptions(chunk)
    const variableTypeByName = new Map(
      getCardStageVariables(chunk).map(variable => [
        variable.name,
        variable.variable_type,
      ])
    )

    return Object.entries(suggestions).reduce((acc, [key, suggestion]) => {
      if (!suggestion) {
        return acc
      }

      if (key.startsWith(REQUEST_WIZARD_AI_WIZARD_SELECT_KEY_PREFIX)) {
        const matchedOption = linkOptions.find(
          option => option.value === suggestion.value
        )

        if (
          matchedOption &&
          isFieldEmpty(values[REQUEST_WIZARD_FIELD_LINK_TYPE])
        ) {
          acc.push([REQUEST_WIZARD_FIELD_LINK_TYPE, matchedOption])
        }

        return acc
      }

      if (!variableTypeByName.has(key)) {
        return acc
      }

      const variableType = variableTypeByName.get(key)

      // file variables store file ids (array); the LLM never returns file values.
      const isUnsupportedType = isRequestWizardFieldFileType(variableType)

      if (!isUnsupportedType && isFieldEmpty(values[key])) {
        acc.push([key, suggestion.value])
      }

      return acc
    }, [])
  }

  return EMPTY_LIST
}

export function getAIWizardGenericErrorNotification({ t }) {
  return {
    id: REQUEST_WIZARD_AI_WIZARD_NOTIFICATION_ID,
    text: t('Wizard.AIWizardGenericErrorKey'),
    type: NOTIFICATION_TYPE.error,
  }
}

export function getAIWizardLimitReachedErrorNotification({ t }) {
  return {
    id: REQUEST_WIZARD_AI_WIZARD_NOTIFICATION_ID,
    text: t('Error.AILimitReachedErrorKey'),
    type: NOTIFICATION_TYPE.warning,
  }
}
